/**
 * Company Dashboard component
 * Main dashboard for company users showing overview and quick actions
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Plus, 
  Briefcase, 
  Users, 
  FileText, 
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  Eye,
  BarChart3
} from 'lucide-react';
import { useCompanyAuth } from '../../../core/contexts/CompanyAuthContext';
import { getCompanyStats } from '../services/companyAuthService';

/**
 * Company Dashboard component
 * 
 * @returns {JSX.Element} Company dashboard
 */
const CompanyDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { companyProfile, isVerified } = useCompanyAuth();
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    pendingApplications: 0
  });
  const [loading, setLoading] = useState(true);
  const [showUnverifiedNotice, setShowUnverifiedNotice] = useState(false);

  // Check if user arrived with unverified flag
  useEffect(() => {
    if (searchParams.get('unverified') === 'true' && !isVerified) {
      setShowUnverifiedNotice(true);
    }
  }, [searchParams, isVerified]);

  // Load company stats
  useEffect(() => {
    const loadStats = async () => {
      if (!companyProfile) return;
      
      try {
        const companyStats = await getCompanyStats(companyProfile.id);
        setStats(companyStats);
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [companyProfile]);

  // Quick action cards data
  const quickActions = [
    {
      title: 'Post New Job',
      description: 'Create a new job posting to attract top talent',
      icon: <Plus className="w-6 h-6" />,
      action: () => navigate('/company/jobs/new'),
      color: 'bg-green-500',
      disabled: !isVerified
    },
    {
      title: 'Manage Jobs',
      description: 'View and edit your existing job postings',
      icon: <Briefcase className="w-6 h-6" />,
      action: () => navigate('/company/jobs'),
      color: 'bg-blue-500',
      disabled: false
    },
    {
      title: 'Review Applications',
      description: 'Review and manage candidate applications',
      icon: <FileText className="w-6 h-6" />,
      action: () => navigate('/company/applications'),
      color: 'bg-purple-500',
      disabled: false
    },
    {
      title: 'Company Profile',
      description: 'Update your company information and branding',
      icon: <Users className="w-6 h-6" />,
      action: () => navigate('/company/profile'),
      color: 'bg-orange-500',
      disabled: false
    }
  ];

  // Stats cards data
  const statsCards = [
    {
      title: 'Total Jobs Posted',
      value: stats.totalJobs,
      icon: <Briefcase className="w-8 h-8 text-blue-500" />,
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Active Job Postings',
      value: stats.activeJobs,
      icon: <TrendingUp className="w-8 h-8 text-green-500" />,
      bgColor: 'bg-green-50'
    },
    {
      title: 'Total Applications',
      value: stats.totalApplications,
      icon: <FileText className="w-8 h-8 text-purple-500" />,
      bgColor: 'bg-purple-50'
    },
    {
      title: 'Pending Reviews',
      value: stats.pendingApplications,
      icon: <Clock className="w-8 h-8 text-orange-500" />,
      bgColor: 'bg-orange-50'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Welcome back, {companyProfile?.companyName}
          </h1>
          <p className="text-gray-600">
            Manage your job postings and connect with top university talent
          </p>
        </div>

        {/* Unverified Account Notice */}
        {showUnverifiedNotice && !isVerified && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-800 mb-1">Account Verification Pending</h3>
                <p className="text-yellow-700 text-sm mb-3">
                  Your company account is currently under review. You can explore the dashboard, 
                  but job posting features will be available after verification is complete.
                </p>
                <button
                  onClick={() => setShowUnverifiedNotice(false)}
                  className="text-yellow-600 hover:text-yellow-800 text-sm font-medium"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Verification Status */}
        <div className="mb-8">
          <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
            isVerified 
              ? 'bg-green-100 text-green-700' 
              : 'bg-yellow-100 text-yellow-700'
          }`}>
            {isVerified ? (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>Verified Company</span>
              </>
            ) : (
              <>
                <Clock className="w-4 h-4" />
                <span>Verification Pending</span>
              </>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statsCards.map((card, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">{card.title}</p>
                  <p className="text-3xl font-bold text-gray-900">{card.value}</p>
                </div>
                <div className={`p-3 rounded-xl ${card.bgColor}`}>
                  {card.icon}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickActions.map((action, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl shadow-sm p-6 transition-all duration-200 ${
                  action.disabled 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:shadow-md cursor-pointer'
                }`}
                onClick={action.disabled ? undefined : action.action}
              >
                <div className={`w-12 h-12 ${action.color} rounded-xl flex items-center justify-center text-white mb-4`}>
                  {action.icon}
                </div>
                <h3 className="font-semibold text-gray-800 mb-2">{action.title}</h3>
                <p className="text-sm text-gray-600">{action.description}</p>
                {action.disabled && (
                  <p className="text-xs text-yellow-600 mt-2 font-medium">
                    Available after verification
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity / Getting Started */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Getting Started Guide */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center space-x-2">
              <BarChart3 className="w-5 h-5 text-green-500" />
              <span>Getting Started</span>
            </h3>
            <div className="space-y-3">
              <div className={`flex items-center space-x-3 p-3 rounded-lg ${
                companyProfile ? 'bg-green-50' : 'bg-gray-50'
              }`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  companyProfile ? 'bg-green-500' : 'bg-gray-300'
                }`}>
                  {companyProfile && <CheckCircle className="w-4 h-4 text-white" />}
                </div>
                <span className={`text-sm ${companyProfile ? 'text-green-700' : 'text-gray-600'}`}>
                  Complete company profile
                </span>
              </div>
              
              <div className={`flex items-center space-x-3 p-3 rounded-lg ${
                isVerified ? 'bg-green-50' : 'bg-gray-50'
              }`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  isVerified ? 'bg-green-500' : 'bg-gray-300'
                }`}>
                  {isVerified && <CheckCircle className="w-4 h-4 text-white" />}
                </div>
                <span className={`text-sm ${isVerified ? 'text-green-700' : 'text-gray-600'}`}>
                  Account verification
                </span>
              </div>
              
              <div className={`flex items-center space-x-3 p-3 rounded-lg ${
                stats.totalJobs > 0 ? 'bg-green-50' : 'bg-gray-50'
              }`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  stats.totalJobs > 0 ? 'bg-green-500' : 'bg-gray-300'
                }`}>
                  {stats.totalJobs > 0 && <CheckCircle className="w-4 h-4 text-white" />}
                </div>
                <span className={`text-sm ${stats.totalJobs > 0 ? 'text-green-700' : 'text-gray-600'}`}>
                  Post your first job
                </span>
              </div>
            </div>
          </div>

          {/* Company Info */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Company Information</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Industry</span>
                <span className="text-sm font-medium">{companyProfile?.industry || 'Not specified'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Company Size</span>
                <span className="text-sm font-medium">{companyProfile?.companySize || 'Not specified'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Location</span>
                <span className="text-sm font-medium">{companyProfile?.location || 'Not specified'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Website</span>
                <span className="text-sm font-medium">
                  {companyProfile?.website ? (
                    <a 
                      href={companyProfile.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-500 hover:text-green-600"
                    >
                      <Eye className="w-4 h-4 inline mr-1" />
                      View
                    </a>
                  ) : 'Not specified'}
                </span>
              </div>
            </div>
            <button
              onClick={() => navigate('/company/profile')}
              className="w-full mt-4 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              Edit Profile
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDashboard;